import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FileText, BookOpen, Wrench, LifeBuoy, Cog, FileSpreadsheet, Download, ExternalLink, Search, X, CheckCircle2, ArrowRight, GraduationCap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { CONTACT } from '../lyphline';
import { AcademyOptIn } from '../components/AcademyOptIn';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', name: 'All Downloads', icon: FileText },
  { id: 'instruction-manual', name: 'Instruction Manuals', icon: BookOpen },
  { id: 'maintenance-guide', name: 'Maintenance Guides', icon: Wrench },
  { id: 'troubleshooting-guide', name: 'Troubleshooting Guides', icon: LifeBuoy },
  { id: 'tools-software', name: 'Tools & Software', icon: Cog },
  { id: 'spec-sheet', name: 'Spec Sheets', icon: FileSpreadsheet },
  { id: 'other', name: 'Other', icon: FileText },
];

const iconFor = (category) => (CATEGORIES.find((c) => c.id === category)?.icon) || FileText;
const nameFor = (category) => (CATEGORIES.find((c) => c.id === category)?.name) || 'Document';

const Documents = () => {
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [gateDoc, setGateDoc] = useState(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      try {
        const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
        const res = await axios.get(`${API_URL}/api/documents${params}`);
        setDocuments(res.data);
      } catch (error) {
        console.error('Failed to fetch documents:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, [selectedCategory]);

  const filteredDocuments = documents.filter((doc) =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (doc.description && doc.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getDocumentUrl = (doc) => {
    if (doc.external_url) return doc.external_url;
    if (doc.file_url) return `${API_URL}${doc.file_url}`;
    return '#';
  };

  const openGate = (doc) => {
    setGateDoc(doc);
    setEmail('');
  };

  const submitGate = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email to get your download.');
      return;
    }
    const url = getDocumentUrl(gateDoc);
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/document`, {
        email,
        document_title: gateDoc.title,
        download_url: gateDoc.external_url ? url : `${window.location.origin.replace(/\/$/, '')}${gateDoc.file_url}`,
        category: gateDoc.category,
      });
    } catch (e) { /* non-blocking */ }
    setSubmitting(false);
    window.open(url, '_blank', 'noopener, noreferrer');
    toast.success('Sent to your inbox, opening now too');
    setGateDoc(null);
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <SEO title="Freeze Dryer Manuals, Guides & Whitepapers" description="Download LyphLine freeze dryer manuals, maintenance guides and rebuild economics whitepapers — free with your email." />
      <Navbar />

      {/* Hero */}
      <div className="pt-40 pb-14 bg-[#003456] relative overflow-hidden" data-testid="documents-hero">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #CE8946 0, transparent 40%), radial-gradient(circle at 80% 70%, #CE8946 0, transparent 45%)' }} />
        <div className="max-w-6xl mx-auto px-6 relative">
          <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">Resource Library</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mt-3 max-w-3xl">Downloads, manuals &amp; guides</h1>
          <p className="text-white/70 text-lg mt-4 max-w-2xl">
            Instruction manuals, maintenance and troubleshooting guides, spec sheets and tools for your freeze dryer, free to download.
          </p>
        </div>
      </div>

      <div className="py-14">
        <div className="max-w-6xl mx-auto px-6">
          {/* Freeze Dry Academy callout */}
          <div className="mb-10 bg-[#003456] rounded-2xl p-6 sm:p-8" data-testid="documents-academy-card">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <span className="w-14 h-14 rounded-xl bg-[#CE8946]/15 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="h-7 w-7 text-[#CE8946]" />
              </span>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white">Want to master your machine, not just fix it?</h2>
                <p className="text-white/70 text-sm mt-1.5">
                  Enter your email to get access to Freeze Dry Academy, operator courses, batch calculators, an AI
                  assistant and a community forum, and we'll keep you posted on rebuild tips and offers.
                </p>
              </div>
            </div>
            <div className="mt-5 sm:max-w-xl">
              <AcademyOptIn variant="dark" source="documents-academy-optin" testid="documents-academy-optin" />
            </div>
            <div className="mt-3">
              <Link to="/freeze-dry-academy" className="inline-flex items-center gap-1.5 text-[#CE8946] hover:text-[#B87333] font-semibold text-sm transition-colors" data-testid="documents-academy-explore">
                See what's inside the Academy <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Search + filter */}
          <div className="mb-10 space-y-4">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search downloads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]"
                data-testid="documents-search"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`text-sm font-medium px-4 py-2 rounded-md border transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-[#003456] text-white border-[#003456]'
                      : 'bg-white text-[#003456] border-slate-300 hover:border-[#CE8946]'
                  }`}
                  data-testid={`documents-cat-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin h-8 w-8 border-4 border-[#003456] border-t-transparent rounded-full mx-auto" />
              <p className="text-slate-500 mt-4">Loading downloads...</p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200" data-testid="documents-empty">
              <FileText className="h-14 w-14 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#003456] mb-2">No downloads found</h3>
              <p className="text-slate-500">
                {searchQuery ? 'Try adjusting your search or filter.' : 'Documents will appear here once the team adds them.'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="documents-grid">
              {filteredDocuments.map((doc) => {
                const Icon = iconFor(doc.category);
                const isExternal = !!doc.external_url;
                return (
                  <div key={doc.id} className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#CE8946] hover:shadow-lg transition-all flex flex-col" data-testid={`document-card-${doc.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#003456] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-[#CE8946]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-[#003456] leading-snug">{doc.title}</h3>
                        <p className="text-slate-500 text-sm mt-1 line-clamp-2">{doc.description || 'No description'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <span className="text-xs px-2.5 py-1 bg-[#F1F5F9] rounded-full text-slate-600">{nameFor(doc.category)}</span>
                      {doc.file_type && <span className="text-xs px-2.5 py-1 bg-[#F1F5F9] rounded-full text-slate-600 uppercase">{doc.file_type}</span>}
                    </div>
                    <button
                      onClick={() => openGate(doc)}
                      className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-center gap-2 w-full text-[#003456] font-bold text-sm hover:text-[#CE8946] transition-colors"
                      data-testid={`document-download-${doc.id}`}
                    >
                      {isExternal ? <ExternalLink className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                      {isExternal ? 'Open link' : 'Download'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Help CTA */}
          <div className="mt-16 bg-[#003456] rounded-2xl p-8 md:p-10 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">Can't find the document you need?</h2>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">
              Tell us your machine model and what you're looking for, we'll send the right manual or guide, or help diagnose the issue.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md transition-colors" data-testid="documents-contact-cta">
                Contact us <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 font-semibold px-6 py-3 rounded-md transition-colors">
                {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Email gate modal */}
      {gateDoc && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="document-gate-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative">
            <button onClick={() => setGateDoc(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="document-gate-close">
              <X className="h-5 w-5" />
            </button>
            <div className="w-12 h-12 bg-[#003456] rounded-xl flex items-center justify-center mb-4">
              <Download className="h-6 w-6 text-[#CE8946]" />
            </div>
            <h3 className="text-xl font-bold text-[#003456]">Get &ldquo;{gateDoc.title}&rdquo;</h3>
            <p className="text-slate-600 text-sm mt-2">Enter your email and we'll send your copy, plus open it right away.</p>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitGate(); }}
              placeholder="you@email.com"
              className="mt-5 w-full px-4 py-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]"
              data-testid="document-gate-email"
            />
            <button
              onClick={submitGate}
              disabled={submitting}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] disabled:opacity-60 text-[#003456] font-bold px-6 py-3 rounded-md transition-colors"
              data-testid="document-gate-submit"
            >
              {submitting ? 'Sending...' : (<><CheckCircle2 className="h-4 w-4" /> Get my download</>)}
            </button>
            <p className="text-slate-400 text-xs mt-3 text-center">We'll only use your email to send your download and helpful freeze-dryer tips.</p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Documents;
